<template>
  <!-- 🔹 Статичный overlay (не анимируется) -->
  <div 
    class="drawer-overlay"
    v-if="visible"
    @click.self="close"
  ></div>

  <!-- 🔹 Анимируемый drawer (единственный элемент в <transition>) -->
  <transition name="slide-left">
    <div 
      v-if="visible"
      class="drawer-wrapper"
    >
      <div class="drawer-content">

        <!-- 🔹 Header -->
        <div class="drawer-header">
          <h2 class="drawer-title">Добавить компанию</h2>
          <button class="close-button" @click="close">&#10005;</button>
        </div>

        <div 
        v-if="isFullStackHRPremiumOpen"
        class="premium-banner"
      >
        ⭐ У вас доступ Fullstack HR Premium — вы можете добавлять несколько компаний
      </div>

        <!-- 🔹 Body -->
        <div class="drawer-body">
          <div class="choose-company-container">

            <!-- Поиск -->
            <div class="top-row">

            <!-- Поиск -->
            <div class="search-bar">
              <img
                :src="isLightTheme ? searchLight : searchDark"
                alt="🔍"
                class="search-icon"
              />
              <input 
                type="text" 
                v-model="searchQuery" 
                placeholder="Давайте найдем вашу компанию"
                class="search-input"
                @input="searchCompanies"
              />
            </div>

            <!-- Кнопка Создать компанию -->
            <button class="create-btn" @click="createCompany">
              + Создать компанию
            </button>

            </div>

            <!-- Loading -->
            <div v-if="isLoading" class="spinner-container">
              <div class="spinner"></div>
            </div>

            <!-- Результаты -->
            <div
              v-if="!isLoading && searchQuery.trim().length > 0 && searchResults.length > 0"
              class="company-grid"
            >
              <CompanyToChoose
                v-for="company in searchResults"
                :key="company.company_id"
                :name="company.name"
                :specifics="company.specifics"
                :logoUrl="company.logo_url"
                :companyId="company.company_id"
                :isSelected="selectedCompanyId === company.company_id"
                @companyClicked="toggleCompanySelection"
              />
            </div>

            <!-- Заглушка: Ничего не найдено -->
            <div
              v-else-if="!isLoading && searchQuery.trim().length > 0 && searchResults.length === 0"
              class="empty-state"
            >
              <img :src="emptySearch" class="empty-icon" alt="empty" />
              <p class="empty-title">Мы ничего не нашли</p>
              <p class="empty-sub">Попробуйте изменить запрос или уточнить название компании</p>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <div class="drawer-footer">
          <button 
            class="save-btn"
            :disabled="!hasSelection"
            @click="updateUserCompany"
          >
            Сохранить
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import CompanyToChoose from "../CompanyToChoose.vue";
import CompanyService from "../../api/CompanyService.js";
import UserService from "../../api/UserService.js";

import searchLight from '../../assets/search_light.png';
import searchDark from '../../assets/search_dark.png';
import emptySearch from '../../assets/empty-vacancies.png';

const isLightTheme = ref(document.documentElement.getAttribute('data-theme') === 'light');
const updateTheme = () => {
  isLightTheme.value = document.documentElement.getAttribute('data-theme') === 'light';
};

const props = defineProps({
  visible: Boolean,
  modelValue: Object,
  isFullStackHRPremiumOpen: { type: Boolean, default: false }
});

const emit = defineEmits(["close", "startOverlay", "stopOverlay", "company-added"]);

const close = () => emit("close");

const searchQuery = ref("");
const searchResults = ref([]);
const selectedCompanyId = ref(null);
const isLoading = ref(false);
const isUpdating = ref(false);

const toggleCompanySelection = (companyId) => {
  console.log("👆 toggleCompanySelection →", companyId);

  // При повторном клике — убрать выделение
  if (selectedCompanyId.value === companyId) {
    selectedCompanyId.value = null;
  } else {
    selectedCompanyId.value = companyId;
  }
};

const hasSelection = computed(() => selectedCompanyId.value !== null);

let searchTimer;
const searchCompanies = async () => {
  clearTimeout(searchTimer);

  const q = searchQuery.value.trim();
  if (!q) {
    searchResults.value = [];
    return;
  }

  searchTimer = setTimeout(async () => {
    isLoading.value = true;
    try {
      searchResults.value = await CompanyService.searchCompanies(q);
    } catch (e) {
      console.error("❌ Ошибка загрузки компаний:", e);
    } finally {
      isLoading.value = false;
    }
  }, 300);
};

const updateUserCompany = async () => {
  if (!selectedCompanyId.value) return;

  emit("startOverlay");
  isUpdating.value = true;

  const minOverlayTime = new Promise(r => setTimeout(r, 800));

  try {
    const selected = searchResults.value.find(
      c => c.company_id === selectedCompanyId.value
    );

    const selectedName = selected?.name || "";

    console.log("🔥 PREMIUM mode:", props.isFullStackHRPremiumOpen);
    console.log("🔥 selectedCompanyId:", selectedCompanyId.value);
    console.log("🔥 selectedName:", selectedName);

    if (props.isFullStackHRPremiumOpen) {

      console.log("📡 Calling: updateUserCompanies (MULTI)");
      await UserService.updateUserCompanies(selectedCompanyId.value, selectedName);
    } else {
      console.log("📡 Calling: updateUserCompany (SINGLE)");
      await UserService.updateUserCompany(selectedCompanyId.value, selectedName);
    }

    const raw = await CompanyService.fetchCompanyById(selectedCompanyId.value);
    const fullCompany = mapCompanyToUI(raw);
    await minOverlayTime;

    emit("company-added", fullCompany);

  } catch (e) {
    console.error("❌ Ошибка обновления компании:", e);
  } finally {
    isUpdating.value = false;
    emit("stopOverlay");
    emit("close");
  }
};

function mapCompanyToUI(raw) {
  return {
    company_id: raw.companyId,
    name: raw.name,
    specifics: raw.specifics,
    logo_url: raw.urlOfLogo,
    cover_url: raw.urlOfBackground,
    region_id: raw.regionId,
    about_company: raw.aboutCompany,
  };
}

onMounted(() => {
  const onEsc = (e) => e.key === 'Escape' && close();
  window.addEventListener('keydown', onEsc);

  const observer = new MutationObserver(updateTheme);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme']
  });

  onUnmounted(() => {
    window.removeEventListener('keydown', onEsc);
    observer.disconnect();
  });
});

const createCompany = () => {
  emit("close");
  const mode = props.isFullStackHRPremiumOpen ? "true" : "false";
  window.location.href = `/create-company?premium=${mode}`;
};

</script>

<style scoped>
/* =========================================================
   OVERLAY + DRAWER
========================================================= */

.drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;

  background: rgba(10, 15, 25, 0.38);
  backdrop-filter: blur(2px);
}

.drawer-wrapper {
  position: fixed;
  inset: 0;
  z-index: 1100;

  display: flex;
  align-items: stretch;
  justify-content: flex-start;

  pointer-events: none;
}

.drawer-content {
  width: min(900px, calc(100vw - 40px));
  height: 100%;

  display: flex;
  flex-direction: column;

  background: #ffffff;
  box-shadow: 8px 0 40px rgba(28, 42, 62, 0.16);

  pointer-events: auto;
}


/* =========================================================
   HEADER
========================================================= */

.drawer-header {
  min-height: 82px;
  padding: 0 30px;

  display: flex;
  align-items: center;
  gap: 20px;

  border-bottom: 1px solid #eceef1;
  flex-shrink: 0;
}

.drawer-title {
  flex: 1;

  margin: 0;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 22px;
  line-height: 1.2;
  color: #171719;
}

.close-button {
  width: 42px;
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;
  border: none;
  border-radius: 50%;

  background: transparent;
  color: #1b1b1d;
  font-family: inherit;
  font-size: 22px;
  line-height: 1;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    transform 0.15s ease;
}

.close-button:hover {
  background: #f1f2f4;
  transform: rotate(3deg);
}


/* =========================================================
   PREMIUM BANNER
========================================================= */

.premium-banner {
  width: auto;
  margin: 18px 30px 0;
  padding: 12px 16px;

  display: flex;
  align-items: center;

  border: 1px solid rgba(40, 135, 247, 0.18);
  border-radius: 14px;

  background: #eef6ff;
  color: #1765c1;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 13px;
  line-height: 1.45;
}


/* =========================================================
   BODY
========================================================= */

.drawer-body {
  flex: 1;
  min-height: 0;

  padding: 28px 30px 40px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.choose-company-container {
  width: 100%;
}


/* =========================================================
   SEARCH + CREATE BUTTON
========================================================= */

.top-row {
  width: 100%;
  margin-bottom: 26px;

  display: flex;
  align-items: center;
  gap: 14px;
}

.search-bar {
  flex: 1;
  min-width: 0;
  height: 54px;

  display: flex;
  align-items: center;

  padding: 0 18px;
  border: 1px solid transparent;
  border-radius: 18px;

  background: #f1f2f4;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.search-bar:hover {
  background: #eceef1;
}

.search-bar:focus-within {
  background: #ffffff;
  border-color: rgba(40, 135, 247, 0.5);
  box-shadow: 0 0 0 4px rgba(40, 135, 247, 0.1);
}

.search-icon {
  width: 22px;
  height: 22px;
  margin-right: 12px;

  object-fit: contain;
  flex-shrink: 0;
  opacity: 0.72;
}

.search-input {
  width: 100%;
  min-width: 0;

  padding: 0;
  border: none;
  outline: none;

  background: transparent;
  color: #1a1a1c;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 15px;
  line-height: 1;
}

.search-input::placeholder {
  color: #92959b;
}

.create-btn {
  height: 54px;
  padding: 0 24px;

  border: none;
  border-radius: 18px;

  background: #2887f7;
  color: #ffffff;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
  white-space: nowrap;

  cursor: pointer;

  box-shadow: 0 7px 18px rgba(40, 135, 247, 0.2);

  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
}

.create-btn:hover {
  background: #1878e9;
  box-shadow: 0 9px 22px rgba(40, 135, 247, 0.28);
  transform: translateY(-1px);
}

.create-btn:active {
  transform: translateY(0);
}


/* =========================================================
   COMPANY RESULTS GRID
========================================================= */

.company-grid {
  width: 100%;

  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  align-items: stretch;
}

/*
  CompanyToChoose — дочерний компонент.
  :deep нужен, чтобы scoped-стили родителя применились к нему.

  Ниже указаны наиболее вероятные названия классов.
  Оставь те селекторы, которые совпадают с CompanyToChoose.vue.
*/

.company-grid :deep(.company-card),
.company-grid :deep(.company-item),
.company-grid :deep(.company-container) {
  width: 100%;
  min-height: 96px;
  box-sizing: border-box;

  display: flex;
  align-items: center;

  margin: 0;
  padding: 16px 18px;

  border: 1px solid transparent;
  border-radius: 18px;

  background: #f7f7f8;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
}

.company-grid :deep(.company-card:hover),
.company-grid :deep(.company-item:hover),
.company-grid :deep(.company-container:hover) {
  background: #ffffff;
  border-color: #d7e8fc;
  box-shadow: 0 9px 26px rgba(31, 63, 103, 0.09);
  transform: translateY(-2px);
}

.company-grid :deep(.company-card.selected),
.company-grid :deep(.company-item.selected),
.company-grid :deep(.company-container.selected),
.company-grid :deep(.selected) {
  background: #edf6ff;
  border-color: #2887f7;
  box-shadow: 0 0 0 3px rgba(40, 135, 247, 0.1);
}

.company-grid :deep(.company-logo),
.company-grid :deep(.logo),
.company-grid :deep(.logo-image) {
  width: 52px;
  height: 52px;
  flex-shrink: 0;

  padding: 5px;
  border: 1px solid #eceef1;
  border-radius: 15px;

  background: #ffffff;
  object-fit: contain;
}

.company-grid :deep(.company-info),
.company-grid :deep(.company-text),
.company-grid :deep(.info) {
  flex: 1;
  min-width: 0;
  margin-left: 14px;
}

.company-grid :deep(.company-name),
.company-grid :deep(.name),
.company-grid :deep(.title) {
  margin: 0 0 5px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  color: #202023;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 15px;
  line-height: 1.3;
}

.company-grid :deep(.company-specifics),
.company-grid :deep(.specifics),
.company-grid :deep(.subtitle) {
  margin: 0;

  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  color: #8b8d93;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.35;
}

.company-grid :deep(.company-arrow),
.company-grid :deep(.arrow),
.company-grid :deep(.chevron) {
  margin-left: 12px;
  flex-shrink: 0;

  color: #8c8e93;

  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.company-grid :deep(.company-card:hover .company-arrow),
.company-grid :deep(.company-item:hover .arrow),
.company-grid :deep(.company-container:hover .chevron),
.company-grid :deep(.selected .company-arrow),
.company-grid :deep(.selected .arrow),
.company-grid :deep(.selected .chevron) {
  color: #2887f7;
  transform: translateX(3px);
}


/* =========================================================
   LOADING
========================================================= */

.spinner-container {
  min-height: 180px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 34px;
  height: 34px;

  border: 3px solid rgba(40, 135, 247, 0.16);
  border-top-color: #2887f7;
  border-radius: 50%;

  animation: drawer-spin 0.65s linear infinite;
}

@keyframes drawer-spin {
  to {
    transform: rotate(360deg);
  }
}


/* =========================================================
   EMPTY STATE
========================================================= */

.empty-state {
  min-height: 330px;
  padding: 40px 20px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  text-align: center;
}

.empty-icon {
  width: 92px;
  height: 92px;
  margin-bottom: 18px;

  object-fit: contain;
  opacity: 0.72;
  filter: saturate(0.85);
}

.empty-title {
  margin: 0 0 8px;

  color: #28282b;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.3;
}

.empty-sub {
  max-width: 340px;
  margin: 0;

  color: #868990;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.5;
}


/* =========================================================
   FOOTER
========================================================= */

.drawer-footer {
  min-height: 74px;
  padding: 12px 30px;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  border-top: 1px solid #eceef1;
  background: rgba(255, 255, 255, 0.96);

  flex-shrink: 0;
}

.save-btn {
  min-width: 130px;
  height: 46px;
  padding: 0 26px;

  border: none;
  border-radius: 15px;

  background: #2887f7;
  color: #ffffff;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;

  cursor: pointer;

  box-shadow: 0 6px 16px rgba(40, 135, 247, 0.2);

  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease,
    transform 0.15s ease;
}

.save-btn:not(:disabled):hover {
  background: #1878e9;
  box-shadow: 0 8px 20px rgba(40, 135, 247, 0.27);
  transform: translateY(-1px);
}

.save-btn:disabled {
  background: #d6d8dc;
  color: #96999f;

  box-shadow: none;
  cursor: not-allowed;
}


/* =========================================================
   DRAWER ANIMATION
========================================================= */

.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
}

.slide-left-enter-to,
.slide-left-leave-from {
  transform: translateX(0);
}


/* =========================================================
   DARK THEME
========================================================= */

html[data-theme="dark"] .drawer-overlay {
  background: rgba(0, 0, 0, 0.62);
}

html[data-theme="dark"] .drawer-content {
  background: #202022;
  box-shadow: 8px 0 42px rgba(0, 0, 0, 0.48);
}

html[data-theme="dark"] .drawer-header,
html[data-theme="dark"] .drawer-footer {
  border-color: #363638;
}

html[data-theme="dark"] .drawer-title {
  color: #f3f3f4;
}

html[data-theme="dark"] .close-button {
  color: #eeeeef;
}

html[data-theme="dark"] .close-button:hover {
  background: #323235;
}

html[data-theme="dark"] .premium-banner {
  background: rgba(75, 151, 255, 0.12);
  border-color: rgba(75, 151, 255, 0.25);
  color: #80b5ff;
}

html[data-theme="dark"] .search-bar {
  background: #303033;
  border-color: transparent;
}

html[data-theme="dark"] .search-bar:hover {
  background: #353538;
}

html[data-theme="dark"] .search-bar:focus-within {
  background: #29292c;
  border-color: rgba(75, 151, 255, 0.65);
  box-shadow: 0 0 0 4px rgba(75, 151, 255, 0.12);
}

html[data-theme="dark"] .search-input {
  color: #f2f2f3;
}

html[data-theme="dark"] .search-input::placeholder {
  color: #929297;
}

html[data-theme="dark"] .create-btn {
  background: #3f91f5;
}

html[data-theme="dark"] .create-btn:hover {
  background: #55a0fa;
}

html[data-theme="dark"] .company-grid :deep(.company-card),
html[data-theme="dark"] .company-grid :deep(.company-item),
html[data-theme="dark"] .company-grid :deep(.company-container) {
  background: #2b2b2e;
  border-color: transparent;
}

html[data-theme="dark"] .company-grid :deep(.company-card:hover),
html[data-theme="dark"] .company-grid :deep(.company-item:hover),
html[data-theme="dark"] .company-grid :deep(.company-container:hover) {
  background: #313135;
  border-color: #3e5877;
  box-shadow: 0 9px 26px rgba(0, 0, 0, 0.25);
}

html[data-theme="dark"] .company-grid :deep(.company-card.selected),
html[data-theme="dark"] .company-grid :deep(.company-item.selected),
html[data-theme="dark"] .company-grid :deep(.company-container.selected),
html[data-theme="dark"] .company-grid :deep(.selected) {
  background: rgba(40, 135, 247, 0.13);
  border-color: #4b97ff;
  box-shadow: 0 0 0 3px rgba(75, 151, 255, 0.1);
}

html[data-theme="dark"] .company-grid :deep(.company-logo),
html[data-theme="dark"] .company-grid :deep(.logo),
html[data-theme="dark"] .company-grid :deep(.logo-image) {
  background: #ffffff;
  border-color: #47474a;
}

html[data-theme="dark"] .company-grid :deep(.company-name),
html[data-theme="dark"] .company-grid :deep(.name),
html[data-theme="dark"] .company-grid :deep(.title) {
  color: #f1f1f2;
}

html[data-theme="dark"] .company-grid :deep(.company-specifics),
html[data-theme="dark"] .company-grid :deep(.specifics),
html[data-theme="dark"] .company-grid :deep(.subtitle) {
  color: #a0a0a5;
}

html[data-theme="dark"] .empty-title {
  color: #eeeeef;
}

html[data-theme="dark"] .empty-sub {
  color: #9c9ca2;
}

html[data-theme="dark"] .empty-icon {
  filter: brightness(0.82) saturate(0.8);
}

html[data-theme="dark"] .drawer-footer {
  background: rgba(32, 32, 34, 0.96);
}

html[data-theme="dark"] .save-btn:disabled {
  background: #3b3b3e;
  color: #77777c;
}


/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 760px) {
  .drawer-content {
    width: 100vw;
  }

  .drawer-header {
    min-height: 72px;
    padding: 0 18px;
  }

  .drawer-title {
    font-size: 19px;
  }

  .drawer-body {
    padding: 20px 18px 32px;
  }

  .top-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-bar,
  .create-btn {
    width: 100%;
  }

  .company-grid {
    grid-template-columns: 1fr;
  }

  .premium-banner {
    margin: 14px 18px 0;
  }

  .drawer-footer {
    padding: 12px 18px;
  }

  .save-btn {
    width: 100%;
  }
}
</style>